import Phaser from 'phaser';
import type { AABB, Faction, Facing } from '../game/ontology';
import type { DeformClipSet } from '@gooddesign/anim-schema';
import type { ControlMap } from '../control/ControlMap';
import { Rig } from './Rig';
import { Hitbox } from '../combat/Hitbox';
import { Hurtbox } from '../combat/Hurtbox';
import { MOVE, newBeltState, stepBelt, type BeltState } from '../physics/belt';
import { actorDepth, reflectionDepth, scaleAt, screenPos } from '../game/ground';
import { type ActorState, clipForState, isLocked } from './states';

// Actor — the unit of gameplay on the belt: a position on the oblique ground
// plane, a Rig (the deforming visual), a State machine mapping motion → Loop,
// and its Hitbox / Hurtbox.
//
// THE ACTOR LIVES IN GROUND SPACE, NOT SCREEN SPACE. It knows (x, z, h): along
// the street, into the street, above the ground. `ground.ts` alone knows how that
// projects, and it is asked once per frame, at draw time.
//
// That separation is the whole reason the actor may SCALE with depth. Its combat
// boxes are ground rectangles — an x-interval crossed with a z-interval — so a
// punch has the same reach at the back of the street as at the front, however
// small the drawing has become. TMNT solved the same problem by never scaling the
// sprite at all. Both are honest; only one of them lets the far end of the street
// look far away.

let nextActorId = 1;

/** How near in depth two actors must be for a strike to connect, world px. Wider
 *  than it looks right: at 45° a small z difference is a large screen offset, and
 *  a beat-'em-up that demands pixel-exact depth alignment is miserable to play. */
const STRIKE_DEPTH = 22;

/** The actor's footprint on the ground, world px. `depth` is its extent in z. */
const BODY_DEPTH = 16;

export interface ActorConfig {
  scene: Phaser.Scene;
  /** The actor's full baked animation set — one DeformClipSet per rig. */
  clips: DeformClipSet;
  /** Spawn, in GROUND coordinates: along the street, and into it. */
  x: number;
  z: number;
  faction: Faction;
  /** Footprint width along the street, world px. */
  bodyWidth: number;
  /** Rig→world scale at the NEAR kerb. Depth scales it from here. */
  rigScale: number;
  /** Player Actors carry a ControlMap; Dummies leave this undefined. */
  control?: ControlMap;
  debugBody?: boolean;
}

export class Actor {
  readonly id = nextActorId++;
  readonly faction: Faction;
  readonly rig: Rig;
  readonly hitbox: Hitbox;
  readonly hurtbox: Hurtbox;

  /** Position on the belt. Read it; move it through update(). */
  readonly belt: BeltState;

  private control?: ControlMap;
  private readonly bodyWidth: number;
  private readonly nearScale: number;
  private readonly debug?: Phaser.GameObjects.Rectangle;

  private state: ActorState = 'idle';
  private stateTime = 0;
  facing: Facing = 'right';

  get stateName(): ActorState {
    return this.state;
  }

  /** Screen position of the actor's feet, for the camera and the HUD. */
  get screen(): { x: number; y: number } {
    return screenPos(this.belt.x, this.belt.z, this.belt.h);
  }

  /** The one place State changes: resets the timer and re-plays the mapped clip,
   *  but only on an actual transition — so a one-shot clip (attack/hit) is never
   *  restarted every frame while its State persists. */
  private setState(next: ActorState): void {
    if (next === this.state) return;
    this.state = next;
    this.stateTime = 0;
    this.rig.play(clipForState(next));
  }

  constructor(cfg: ActorConfig) {
    this.faction = cfg.faction;
    this.control = cfg.control;
    this.bodyWidth = cfg.bodyWidth;
    this.nearScale = cfg.rigScale;
    this.belt = newBeltState(cfg.x, cfg.z);

    // Rig — lit by the scene's light rig (normal-mapped) so the night-alley key
    // crosses the actor as it moves. `reflection` builds the mirrored copy; the
    // Scene decides each frame how wet the ground beneath this actor is.
    this.rig = new Rig(cfg.scene, cfg.clips, { scale: cfg.rigScale, lit: true, reflection: true, shadow: true });

    // Combat boxes are GROUND rectangles: `x`/`width` run along the street,
    // `y`/`height` are the z-interval into it. They reuse the AABB type and the
    // AABB overlap test unchanged — only the meaning of the second axis moved.
    this.hitbox = new Hitbox(this.id, cfg.faction, { x: 0, y: 0, width: 30, height: STRIKE_DEPTH });
    this.hurtbox = new Hurtbox(this.id, cfg.faction, { x: 0, y: 0, width: cfg.bodyWidth, height: BODY_DEPTH });
    this.hurtbox.onHit = () => this.react();

    this.debug = cfg.debugBody
      ? cfg.scene.add.rectangle(0, 0, cfg.bodyWidth, 6, 0x4684b2, 0.4).setDepth(9)
      : undefined;

    this.syncBoxes();
    this.draw();
    this.rig.play('idle');
  }

  /** Feet position along/into the street. */
  get x(): number { return this.belt.x; }
  get z(): number { return this.belt.z; }
  get h(): number { return this.belt.h; }

  private faceDir(): 1 | -1 {
    return this.facing === 'right' ? 1 : -1;
  }

  /** Enter the reaction State — the hurtbox hit path. */
  private react(): void {
    this.hitbox.deactivate();
    this.setState('hit');
    // A little knockback along the street (deterministic, fixed distance).
    this.belt.x -= this.faceDir() * 10;
  }

  update(dtSec: number): void {
    if (this.control) this.updatePlayer(dtSec);
    else this.updateDummy(dtSec);

    this.hurtbox.update(dtSec);
    this.syncBoxes();
    this.draw();
    this.rig.update(dtSec);
  }

  private updatePlayer(dtSec: number): void {
    const ctrl = this.control!;
    const step = stepBelt(this.belt, ctrl, dtSec);

    // Facing follows ALONG-STREET intent only. Walking upstage never turns you
    // around — that is the genre's rule, and it is why `depthDir` is ignored here.
    if (step.moveDir < 0) this.facing = 'left';
    else if (step.moveDir > 0) this.facing = 'right';

    this.stateTime += dtSec;

    // Locked States (attack / hit) own the Actor until their Loop finishes.
    if (isLocked(this.state)) {
      if (this.state === 'attack') {
        const active = this.stateTime > 0.05 && this.stateTime < 0.24;
        if (active && !this.hitbox.active) this.hitbox.activate();
        if (!active && this.hitbox.active) this.hitbox.deactivate();
      }
      if (this.rig.isDone()) {
        this.hitbox.deactivate();
        this.enterMotionState(step);
      }
      return;
    }

    if (ctrl.get('attack').pressed && step.grounded) {
      this.setState('attack');
      return;
    }

    this.enterMotionState(step);
  }

  /** Pick idle / run / jump / fall from the belt's motion. */
  private enterMotionState(step: { grounded: boolean; moving: boolean }): void {
    let next: ActorState;
    if (!step.grounded) {
      next = this.belt.vh > 0 ? 'jump' : 'fall';
    } else if (step.moving) {
      next = 'run';
    } else {
      next = 'idle';
    }
    this.setState(next);
  }

  private updateDummy(dtSec: number): void {
    this.stateTime += dtSec;
    if (this.state === 'hit' && this.rig.isDone()) this.setState('idle');
  }

  /** Project the actor onto the screen: place, mirror, SCALE by depth, and sort.
   *  Everything in here is presentation; none of it feeds back into the belt. */
  private draw(): void {
    const p = this.screen;
    const s = scaleAt(this.belt.z) * this.nearScale;
    this.rig.scale(s);
    this.rig.face(this.faceDir());
    this.rig.place(p.x, p.y);
    this.rig.depth(actorDepth(this.belt.z), reflectionDepth(this.belt.z));
    this.rig.shadeDepth(actorDepth(this.belt.z));
    this.debug?.setPosition(p.x, p.y);
  }

  /** Refresh the ground-space Hit/Hurt rectangles from (x, z) and facing. */
  private syncBoxes(): void {
    const { x, z } = this.belt;

    this.hurtbox.box = {
      x: x - this.bodyWidth / 2,
      y: z - BODY_DEPTH / 2,
      width: this.bodyWidth,
      height: BODY_DEPTH,
    };

    // The hitbox projects in front along the street, at the actor's own depth.
    const w = this.hitbox.box.width;
    const hx = this.faceDir() > 0 ? x + this.bodyWidth / 2 : x - this.bodyWidth / 2 - w;
    this.hitbox.box = { x: hx, y: z - STRIKE_DEPTH / 2, width: w, height: STRIKE_DEPTH } as AABB;
  }

  /** How wet the ground under this actor is. Set by the Scene each frame. */
  reflect(strength: number, planeY: number): void {
    this.rig.reflect(strength, planeY);
  }

  /** How this actor throws its cast shadow this frame — the sun's rake and the
   *  shadow opacity (0 in a shade zone, where the sun never reaches). */
  castShadow(dx: number, dy: number, strength: number, softness = 0): void {
    this.rig.castShadow(dx, dy, strength, softness);
  }

  /** How deep in the plate's shadow this actor stands this frame (0..1). */
  setShade(strength: number): void {
    this.rig.shade(strength);
  }

  destroy(): void {
    this.rig.destroy();
    this.debug?.destroy();
  }
}

export { MOVE };
